import * as React from 'react';
import {useState} from 'react';
import {useAppForm} from "components/forms/form";
import {useAuth} from "features/authentication/hooks/useAuth";
import {RealEstateAgent} from "features/template/viewModels/ResidenceStage";
import {InterestFormHeader} from "components/forms/InterestForm/_interestFormHeader";
import {getFormattedMessage} from "features/services/utils/language-helper";
import {CookieStore} from "features/cookies/cookie-store";
import {useAddInterest, useAddInterestAnonymous, useAddInterestPremises} from "features/authentication/services/InterestService/mutation";
import {useSessionStorage} from "components/hooks/useSessionStorage";
import {AddInterestAnonymousParams} from "features/authentication/types.services";
import {PooledInterestKey} from "features/authentication/services/InterestService/config";
import {InterestFormDone} from "components/forms/InterestForm/_interestFormDone";
import {getCurrentSite, getSiteSpecificConfig} from "features/services/utils/env-helper";
import {SITE, HeadlineLevel} from "features/ui/enums";
import {A} from "@atoms/LinksAndButtons/A";
import {FormattedMessage} from "react-intl";
import {P} from "@atoms/Text/P";
import {ContentFold} from "@organisms/GeneralComponents/ContentFold";
import {BedRoomCounter} from "components/forms/InterestForm/_bedroomCounter";
import {getUserInterestFormData} from "components/forms/InterestForm/helpers";
import {
    emailValidation,
    firstNameValidation,
    InterestFormValidationSchema,
    lastNameValidation,
    messageValidation,
    phoneValidation,
    postalCodeValidation,
    termsValidation
} from "components/forms/InterestForm/interestFormValidationSchema";
import {gtmEventsInterestSignupSubmit} from "features/services/googleTagManager/events";
import {Headline} from '@atoms/Text/Headline';

interface Props {
    stageId: string;
    stageName: string;
    stageStatus: string;
    objectId?: string;
    objectStatus?: string;
    premises?: boolean;
    className?: string;

    hideMessageField?: boolean;
    agent?: RealEstateAgent;
    customFormText?: string;
    labelPrefix?: string;

    // OANVÄNDA: deklarerade men aldrig destrukturerade/använda nedan. "Klart"-läget styrs av
    // mutationens isSuccess (renderar InterestFormDone), inte av dessa props. Kandidat för borttagning
    // (passas in från InterestFormModal). (grep-audit 2026-07-02)
    interestDone?: boolean;
    setInterestDone?: (done: boolean) => void;

    onSubmitCallback?: () => void;
}

export const InterestForm : React.FunctionComponent<Props> = ({stageName, customFormText, objectStatus, stageStatus, stageId, objectId, premises, agent, className, labelPrefix}) => {

    const {user, isLoggedIn} = useAuth();
    const currentSite = getCurrentSite();

    const {storedValue: _pooledInterest, removeValue: _removePooledInterest} = useSessionStorage<AddInterestAnonymousParams>(PooledInterestKey);

    const {mutateAsync: addInterest, isPending, isSuccess} = useAddInterest();
    const {mutateAsync: addAnonymousInterest, isPending: isPendingAnon, isSuccess: isSuccessAnon} = useAddInterestAnonymous();
    const {mutateAsync: addPremisesInterest, isPending: isPendingPremises, isSuccess: isSuccessPremises} = useAddInterestPremises();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const interestForm = useAppForm({
        defaultValues: getUserInterestFormData(user),
        onSubmit: async ({value}) => {
            try {
                if (premises) {

                    const {terms: _terms, ...filteredValue} = value;

                    await addPremisesInterest({
                        ...filteredValue,
                        stageId: stageId,
                        objectId: objectId,
                        source: CookieStore.getSourceMedium(),
                    });

                } else if (!isLoggedIn) {
                    const {terms: _terms2, ...filteredValue} = value;

                    await addAnonymousInterest({
                        ...filteredValue,
                        stageId: stageId,
                        objectId: objectId,
                        source: CookieStore.getSourceMedium(),
                    });
                } else {
                    const {terms: _terms3, firstName: _firstName, lastName: _lastName, phone, email, ...filteredValue} = value;

                    await addInterest({
                        ...filteredValue,
                        source: CookieStore.getSourceMedium(),
                        formEmail: user?.email || email,
                        formPhone: user?.phoneNumber || phone,
                        stageId: stageId,
                        objectId: objectId,
                    });
                }

                gtmEventsInterestSignupSubmit(objectId ? 'object' : 'default')();
            } catch {
                // Mutation error handling is done via React Query callbacks
            }
        },
        validators: {
            onSubmit: InterestFormValidationSchema,
        }
    });

    const getTermsLabel = () => {
        const termKeys = {
            [SITE.SENIORGARDEN]: 'jm.components.InterestForm.acceptTermsLabelSeniorgarden',
            [SITE.BORATT]: 'jm.components.InterestForm.acceptTermsLabelBoratt',
        };
        return getFormattedMessage(termKeys[currentSite] || 'jm.components.InterestForm.acceptTermsLabel');
    };

    const renderSiteSpecificTermsLink = () => {
        const gdprLink = getSiteSpecificConfig().gdprLink;

        const termMessageKeys = {
            [SITE.BORATT]: 'jm.components.InterestFormTextInModal.readTermsBoratt',
            [SITE.SENIORGARDEN]: 'jm.components.InterestFormTextInModal.readTermsSeniorgarden',
        };

        return (
            <A
                href={gdprLink}
                target="_blank"
                title={getFormattedMessage(termMessageKeys[currentSite] || 'jm.components.InterestFormTextInModal.readTerms')}
            >
                <FormattedMessage id={termMessageKeys[currentSite] || 'jm.components.InterestFormTextInModal.readTerms'} />
            </A>
        );
    };

    /*
    useEffect(() => {
        if(pooledInterest !== null && user) {
            let pooledTempInterest = pooledInterest;
            removePooledInterest();
            addInterest(pooledTempInterest);
        }
    }, [pooledInterest]);

     */

    if(user?.hasInterest(stageId, objectId) || isSuccess || isSuccessAnon || isSuccessPremises) return <InterestFormDone stageName={stageName} premises={premises} agent={agent} className={className} />;

    return (
        <div className={`interest-form ${className}`}>
            <div className="interest-form__content">

                <InterestFormHeader
                    stageName={stageName}
                    customFormText={customFormText}
                    objectStatus={objectStatus}
                    stageStatus={stageStatus}
                />

                <form onSubmit={(e) => {
                    e.preventDefault();
                    if (isSubmitting) return; // Prevent multiple submissions
                    setIsSubmitting(true);
                    interestForm.handleSubmit().finally(() => setIsSubmitting(false));
                }}>
                    <div className="form-row">
                        <div className="form-column">
                            <interestForm.AppField
                                name="firstName"
                                validators={{
                                    onChange: firstNameValidation
                                }}
                                children={(field) => <field.TextField prefix={labelPrefix} label={getFormattedMessage('jm.components.InterestForm.firstname')}  />}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-column">
                            <interestForm.AppField
                                name="lastName"
                                validators={{
                                    onChange: lastNameValidation
                                }}
                                children={(field) => <field.TextField prefix={labelPrefix} label={getFormattedMessage('jm.components.InterestForm.lastname')}  />}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-column">
                            <interestForm.AppField
                                name="email"
                                validators={{
                                    onChange: emailValidation
                                }}
                                children={(field) => <field.TextField prefix={labelPrefix} label={getFormattedMessage('jm.components.InterestForm.email')}  />}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-column">
                            <interestForm.AppField
                                name="phone"
                                validators={{
                                    onChange: phoneValidation
                                }}
                                children={(field) => <field.TextField prefix={labelPrefix} label={getFormattedMessage('jm.components.InterestForm.phone')}  />}
                            />
                        </div>
                    </div>

                    {(currentSite === SITE.NO && !objectId) &&
                        <div className="form-row">
                            <div className="form-column">
                                <interestForm.AppField
                                    name="postalCode"
                                    validators={{
                                        onChange: postalCodeValidation
                                    }}
                                    children={(field) => <field.TextField prefix={labelPrefix} label={getFormattedMessage('jm.components.InterestForm.postalCode')}  />}
                                />
                            </div>
                        </div>
                    }

                    <div className="form-row">
                        <div className="form-column">
                            <interestForm.AppField
                                name="message"
                                validators={{
                                    onChange: messageValidation
                                }}
                                children={(field) => <field.TextArea prefix={labelPrefix} label={getFormattedMessage('jm.components.InterestForm.message')}  />}
                            />
                        </div>
                    </div>

                    {!user &&
                        <>
                            <div className="form-row form-row--centered">
                                <interestForm.AppField
                                    name="terms"
                                    validators={{
                                        onChange: termsValidation
                                    }}
                                    children={(field) => <field.Checkbox prefix={labelPrefix} label={getTermsLabel()}  />}
                                />
                            </div>
                            <div className="form-row terms-link">
                                {renderSiteSpecificTermsLink()}
                            </div>
                        </>
                    }

                    {(currentSite === SITE.NO && !objectId) &&
                        <ContentFold title={getFormattedMessage('jm.components.InterestForm.wishHeader')} id="interest-form__whishes">
                            <P center>
                                <FormattedMessage id="jm.components.InterestForm.wishContent" />
                            </P>

                            <div className="form-row form-row--column">
                                <Headline level={HeadlineLevel.H5}><FormattedMessage id="jm.components.InterestForm.bedroomHeader" /></Headline>

                                <div>
                                    <BedRoomCounter form={interestForm}  />
                                </div>
                            </div>
                        </ContentFold>
                    }


                    <div className="form-row form-row--centered form-row--small-margin">
                        <interestForm.AppForm>
                            <interestForm.SubscribeButton  loading={isSubmitting || isPending || isPendingAnon || isPendingPremises} label={getFormattedMessage('jm.components.InterestForm.send')} />
                        </interestForm.AppForm>
                    </div>
                </form>
            </div>
        </div>
    )
};
